import { last } from 'lodash';

import { IngredientType } from '@libs/trip-planner-interfaces';
import type {
  AvailableFerryIngredientResponse,
  AvailableFlightIngredientResponse,
  AvailableTransportIngredientResponse,
  FerryIngredientAdapter,
  FlightIngredientAdapter,
  GraphQL,
  HotelIngredientAdapter,
  OwnStayIngredientAdapter,
  OwnTransportIngredientAdapter,
  RebookableOption,
  TransportIngredientAdapter,
  TransportListResponse,
} from '@libs/trip-planner-interfaces';
import { assertValue, normalizeDate } from '@libs/utils';
import { SemboDate } from '@libs/sembo-dates';

import { assertSmartSearchList, assertSmartSearchValue } from '../../utils';

import { toLeg } from './legResponseQL';
import { toMoney } from './moneyResponseQL';

export function toTransportAvailabilityResponse(
  transportAvailabilityResponseQL: GraphQL.getTransportAvailabilityQuery,
  ingredientAdapter:
    | OwnStayIngredientAdapter
    | HotelIngredientAdapter
    | TransportIngredientAdapter
    | OwnTransportIngredientAdapter
): TransportListResponse {
  assertSmartSearchValue(transportAvailabilityResponseQL.getTransportList);
  const transportAvailabilityResponse =
    transportAvailabilityResponseQL.getTransportList;

  assertSmartSearchList(transportAvailabilityResponse.transports);

  return {
    totalTransports: transportAvailabilityResponse.totalTransports,
    transports: transportAvailabilityResponse.transports.reduce(
      (
        list: (
          AvailableFlightIngredientResponse | AvailableFerryIngredientResponse
        )[],
        transportListResult
      ) => {
        if (ingredientAdapter.ingredientType === IngredientType.Flight) {
          const response = toFlightIngredientResponse(
            transportListResult,
            ingredientAdapter
          );

          list.push(response);
        } else if (ingredientAdapter.ingredientType === IngredientType.Ferry) {
          const response = toFerryIngredientResponse(
            transportListResult,
            ingredientAdapter
          );

          list.push(response);
        } else {
          throw new Error(
            'Impossible code path reached! TransportListResult should be mapped to transport in recipe'
          );
        }

        return list;
      },
      []
    ),
  };
}

type TransportAvailabilityResponseQL = NonNullable<
  GraphQL.getTransportAvailabilityQuery['getTransportList']
>;
type TransportListQL = NonNullable<
  TransportAvailabilityResponseQL['transports']
>;
type TranpsportResultQL = NonNullable<TransportListQL[number]>;
function toFlightIngredientResponse(
  transportResultQL: TranpsportResultQL,
  ingredientAdapter: FlightIngredientAdapter
): AvailableFlightIngredientResponse {
  const transportIngredientResponse = toTransportIngredientResponse(
    transportResultQL,
    ingredientAdapter
  );

  const leg = transportIngredientResponse.legs[transportResultQL.legIndex];

  assertValue(
    leg,
    'search-service-smart-search > transportAvailabilityResponseQL > toFlightIngredientResponse > leg'
  );

  return {
    numberOfStops: leg.segments.length - 1,
    isPromoted: transportResultQL.isPromoted,
    ...transportIngredientResponse,
  };
}

function toFerryIngredientResponse(
  transportResultQL: TranpsportResultQL,
  ingredientAdapter: FerryIngredientAdapter
): AvailableFerryIngredientResponse {
  const transportIngredientResponse = toTransportIngredientResponse(
    transportResultQL,
    ingredientAdapter
  );

  const leg = transportIngredientResponse.legs[transportResultQL.legIndex];

  assertValue(
    leg,
    'search-service-smart-search > transportAvailabilityResponseQL > toFerryIngredientResponse > leg'
  );

  const firstSegment = leg.segments[0];

  assertValue(
    firstSegment,
    'search-service-smart-search > transportAvailabilityResponseQL > toFerryIngredientResponse > firstSegment'
  );

  const vehicles = firstSegment.vehicles;

  if (!vehicles) {
    throw new Error('FerryIngredientResponse should always have vehicles');
  }

  const transportationName = firstSegment.transportationName;
  const isCabinMandatory = firstSegment.isCabinMandatory;

  return {
    vehicles,
    transportationName,
    isCabinMandatory,
    ...transportIngredientResponse,
  };
}

function toTransportIngredientResponse(
  transportResultQL: TranpsportResultQL,
  ingredientAdapter: TransportIngredientAdapter
): AvailableTransportIngredientResponse {
  assertSmartSearchList(transportResultQL.legs);
  const legFragment = transportResultQL.legs[transportResultQL.legIndex];
  assertValue(
    legFragment,
    'search-service-smart-search > mappers.ts > toAvailableTransportIngredientResponse > leg'
  );

  const leg = toLeg(legFragment);

  const transportIds: string[] = [];
  const carriers: string[] = [];

  leg.segments.forEach((segment) => {
    transportIds.push(segment.transportId);
    carriers.push(segment.carrier);
  });

  const firstSegment = leg.segments[0];
  const lastSegment = last(leg.segments);

  if (!firstSegment || !lastSegment) {
    throw new Error('Leg segment not found');
  }

  const date = normalizeDate(
    new SemboDate(firstSegment.departureDateTime).startOf('day').get()
  );

  const legs = transportResultQL.legs.map(toLeg);

  assertSmartSearchValue(transportResultQL.totalPrice);
  const totalPrice = toMoney(transportResultQL.totalPrice);
  assertSmartSearchValue(transportResultQL.addToBasketToken);

  return {
    addToBasketToken: transportResultQL.addToBasketToken,
    allCarriers: [],
    alternativeSuggestion: null,
    arrivalDate: new Date(lastSegment.arrivalDateTime),
    arrivalIata: lastSegment.arrivalIata,
    carriers,
    date,
    departureDate: new Date(firstSegment.departureDateTime),
    departureIata: firstSegment.departureIata,
    durationInMinutes: leg.durationInMinutes,
    ingredientId: ingredientAdapter.ingredientId,
    ingredientType: ingredientAdapter.ingredientType,
    isAvailable: true,
    legIndex: transportResultQL.legIndex,
    legs,
    totalPrice,
    transportIds,

    durationRangeInMinutes: undefined,
    platingCarrier: leg.platingCarrier,
    rebookableOption: toRebookableOption(transportResultQL.rebookableOption),
  };
}

function toRebookableOption(
  rebookableOptionQL: TranpsportResultQL['rebookableOption']
): RebookableOption | null {
  if (!rebookableOptionQL) {
    return null;
  }

  if (
    !rebookableOptionQL.rebookablePrice ||
    !rebookableOptionQL.rebookableTotalPrice ||
    !rebookableOptionQL.addToBasketToken
  ) {
    return null;
  }

  return {
    addToBasketToken: rebookableOptionQL.addToBasketToken,
    rebookablePrice: toMoney(rebookableOptionQL.rebookablePrice),
    rebookableTotalPrice: toMoney(rebookableOptionQL.rebookableTotalPrice),
  };
}
